import { useQuery, type QueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { type ReactNode, useEffect, useState } from "react";
import { Uuid } from "spacetimedb";

import { Button } from "@/web/components/primitives/button";
import { Input } from "@/web/components/primitives/input";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { ErrorHandler } from "@/web/lib/errors";
import { callShareableCard } from "@/web/lib/spacetime-server";
import { isAuthError, isNotFoundError } from "@/web/lib/spacetimedbErrors";

// ===== server fn =====
//
// Runs server-side during SSR (Worker → SpacetimeDB HTTP) and is RPC'd from the
// client for subsequent navigations / password-gated retries. `callShareableCard`
// lives in a `@tanstack/react-start/server-only` module, so the start plugin
// strips its implementation from the client bundle and replaces this handler
// with a network stub at build time.
export const fetchCardServerFn = createServerFn({ method: "POST" })
  .inputValidator((d: { slug: string; accessCode: string }) => d)
  .handler(async ({ data }) => callShareableCard(data.slug, data.accessCode));

export const cardQueryKey = (slug: string, accessCode: string) =>
  ["shareable-business-card", slug, accessCode] as const;

// ===== access code persistence =====
// Today the slug is the `shareableBusinessCards_v2` row's UUIDv7 in canonical
// string form — see `createShareableBusinessCard` in
// `spacetimedb/src/sharing/businessCards/external.ts`.
const accessCodeStorageKey = (slug: string) => `shareable-business-card-access-code:${slug}`;

function readPersistedAccessCode(slug: string): string {
  try {
    return window.localStorage.getItem(accessCodeStorageKey(slug)) ?? "";
  } catch (error: unknown) {
    ErrorHandler.logError(error);
    return "";
  }
}

function persistAccessCode(slug: string, code: string) {
  try {
    window.localStorage.setItem(accessCodeStorageKey(slug), code);
  } catch (error: unknown) {
    ErrorHandler.logError(error);
  }
}

function clearPersistedAccessCode(slug: string) {
  try {
    window.localStorage.removeItem(accessCodeStorageKey(slug));
  } catch (error: unknown) {
    ErrorHandler.logError(error);
  }
}

export function parseCardId(slug: string): Uuid | undefined {
  // UUIDv7 canonical form: 8-4-4-4-12 hex chars, version nibble = 7. We
  // accept any valid UUID shape; the server-side lookup is the authority on
  // whether the row exists.
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug)) {
    return undefined;
  }
  try {
    return Uuid.parse(slug);
  } catch (error: unknown) {
    // We've already validated the canonical UUID shape above, so this is a
    // belt-and-suspenders parse-failure path. Log so we know if the SDK's
    // accepted shape ever drifts from our regex.
    ErrorHandler.logError(error);
    return undefined;
  }
}

// ===== loader helper =====
//
// Preloads the anonymous (accessCode === "") fetch into the query cache so the
// password gate / card content can render synchronously during SSR. Auth and
// not-found errors are expected outcomes and swallowed; the component renders
// the appropriate state from the same error shape on the client.
export async function prefetchShareableCard(queryClient: QueryClient, slug: string) {
  await queryClient
    .ensureQueryData({
      queryKey: cardQueryKey(slug, ""),
      queryFn: () => fetchCardServerFn({ data: { slug, accessCode: "" } }),
      retry: false,
    })
    .catch((error: unknown) => {
      if (isAuthError(error) || isNotFoundError(error)) return;
      ErrorHandler.logError(error);
    });
}

// ===== password gate UI =====

export function PasswordGate({
  onSubmit,
  hasError,
}: {
  onSubmit: (code: string) => void;
  hasError: boolean;
}) {
  const [code, setCode] = useState("");
  const trimmed = code.trim();
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="withus-strong text-2xl">Password required</h1>
      <p className="text-sm text-muted-foreground">
        Enter the password the sender shared with you.
      </p>
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) onSubmit(trimmed);
        }}
      >
        <Input
          type="password"
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-invalid={hasError}
          placeholder="Password"
        />
        {hasError ? <p className="text-xs text-destructive">That password didn't match.</p> : null}
        <Button type="submit" color="blue" disabled={trimmed.length === 0}>
          Unlock
        </Button>
      </form>
    </div>
  );
}

// ===== gate hook =====
//
// Drives the password / fetch lifecycle for a single card slug:
//   • Reads the persisted access code after mount (skipped during SSR to
//     avoid hydration mismatch).
//   • Fetches the card with the current access code via the shared server fn.
//   • Persists the code on success, clears it on auth failure.
//
// The hook returns the raw query plus the current access code and a setter
// callers can pass to <PasswordGate onSubmit={...} />.
export function useShareableCardGate(slug: string) {
  const cardId = parseCardId(slug);

  // Initialize empty to keep SSR and first client render in sync; pull from
  // localStorage after mount.
  const [accessCode, setAccessCode] = useState("");
  useEffect(() => {
    const stored = readPersistedAccessCode(slug);
    if (stored) setAccessCode(stored);
  }, [slug]);

  const queryKey = cardQueryKey(slug, accessCode);

  const cardQuery = useQuery({
    queryKey,
    queryFn: () => fetchCardServerFn({ data: { slug, accessCode } }),
    enabled: cardId !== undefined,
    retry: false,
  });

  useEffect(() => {
    if (cardQuery.isSuccess && accessCode.length > 0) {
      persistAccessCode(slug, accessCode);
    }
  }, [cardQuery.isSuccess, accessCode, slug]);

  useEffect(() => {
    if (isAuthError(cardQuery.error)) {
      clearPersistedAccessCode(slug);
    }
  }, [cardQuery.error, slug]);

  return { cardId, accessCode, setAccessCode, cardQuery, queryKey } as const;
}

// ===== gate wrapper =====
//
// Renders `children` once a card is accessible (no password set, or the
// persisted/entered password unlocked it). Shows <PasswordGate /> when the
// card is password-protected and not yet unlocked. Used by routes that only
// need the gate semantics and don't consume the fetched card data — see
// `routes/community-theater/index.tsx`. For routes that render the card
// itself (e.g. `routes/ttw/$slug.tsx`), call `useShareableCardGate` directly.
export function ShareableCardGate({ slug, children }: { slug: string; children: ReactNode }) {
  const { cardId, cardQuery, accessCode, setAccessCode } = useShareableCardGate(slug);

  // If the configured slug isn't a valid UUID we can't gate, so fall through
  // to the page rather than blocking on a permanent "not found".
  if (cardId === undefined) return <>{children}</>;

  if (isAuthError(cardQuery.error)) {
    return <PasswordGate onSubmit={setAccessCode} hasError={accessCode.length > 0} />;
  }
  // A not-found or other fetch failure shouldn't block the page either — the
  // gate exists to enforce a password when one is set, not to hide the page
  // if the card lookup itself goes sideways.
  if (cardQuery.isLoading) return <FullPageLoader />;
  return <>{children}</>;
}
